/**
 * 메인 레이아웃 컴포넌트
 * - Sidebar + Header + Content 조합
 * - 반응형 처리 (모바일/태블릿/데스크톱)
 * - 브레이크포인트별 사이드바 상태 관리
 */

import React, { useState, useEffect } from 'react';
import { Layout } from 'antd';
import { AppSidebar } from './AppSidebar';
import { AppHeader } from './AppHeader';
import { MainLayoutProps } from './types';

const { Content } = Layout;

/**
 * 반응형 브레이크포인트
 */
const BREAKPOINTS = {
  MOBILE: 768,
  TABLET: 1024,
};

/**
 * 메인 레이아웃 컴포넌트
 */
export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  // 사이드바 접힘 상태
  const [collapsed, setCollapsed] = useState(false);
  // 모바일 환경 여부
  const [isMobile, setIsMobile] = useState(false);

  /**
   * 윈도우 리사이즈 감지 및 반응형 처리
   */
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      // 모바일 여부 판단 (< 768px)
      const mobile = width < BREAKPOINTS.MOBILE;
      setIsMobile(mobile);

      // 초기 collapsed 상태 설정
      if (mobile) {
        // 모바일: 기본적으로 닫힘 (Drawer 모드)
        setCollapsed(true);
      } else if (width < BREAKPOINTS.TABLET) {
        // 태블릿: 접힘
        setCollapsed(true);
      } else {
        // 데스크톱: 펼침
        setCollapsed(false);
      }
    };

    // 초기 실행
    handleResize();

    // 리사이즈 이벤트 리스너 등록
    window.addEventListener('resize', handleResize);

    // 클린업
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /**
   * 사이드바 토글 핸들러
   */
  const handleToggleSidebar = () => {
    setCollapsed((prev) => !prev);
  };

  /**
   * 사이드바 접기/펼치기 핸들러
   */
  const handleCollapse = (value: boolean) => {
    setCollapsed(value);
  };

  /**
   * 컨텐츠 영역 좌측 마진 계산
   * - 데스크톱/태블릿: Sider 너비만큼
   * - 모바일: 0 (Drawer 모드)
   */
  const contentMarginLeft = isMobile ? 0 : collapsed ? 80 : 256;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 사이드바 */}
      <AppSidebar
        collapsed={collapsed}
        onCollapse={handleCollapse}
        isMobile={isMobile}
      />

      {/* 메인 레이아웃 (헤더 + 컨텐츠) */}
      <Layout
        style={{
          marginLeft: contentMarginLeft,
          transition: 'margin-left 0.2s',
        }}
      >
        {/* 헤더 */}
        <AppHeader onToggleSidebar={handleToggleSidebar} />

        {/* 컨텐츠 영역 */}
        <Content
          style={{
            margin: '24px',
            padding: '24px',
            background: '#fff',
            borderRadius: '8px',
            minHeight: 'calc(100vh - 64px - 48px)', // 전체 높이 - 헤더 - 여백
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};
